import { Card, List, ListStyle, Tab, View } from '@yleisradio/areena-types';
import { getEpisodes, getList } from '../../service/areena-api/fetchers';
import { isList, isNavigator } from '../../client/typeGuards';
import { Dispatch } from '../../client/configureStore';
import { Episode, EpisodesList, EpisodesSeason } from './types';
import {
  getContinueCardUiProgressBarFromLabels,
  getLiveCardUiProgressBar,
  isCurrentBroadcastCard,
} from '../../client/utils/cardProgress';
import { getUrlAndCache, imageVariations } from '../../client/View/imageCache';
import { ApplicationResolution } from '../../client/applicationResolution';
import { getEpisodesSelector, getListSelector } from './selectors';
import {
  GeoLocation,
  NavigateFunctionWithState,
} from '../../client/appTypings';
import { createAsyncThunk } from '../createAsyncThunk';
import { idFromPointer } from '../../client/utils/pointer';
import { AuthFetch } from '../../client/domain/AuthFetch';
import { routePathsById } from '../../client/Routes/getRoutePath';
import { getIsGeoBlocked, getRegion } from '../../client/utils/geoBlock';
import { NotFoundError } from '../../client/error/errors';
import { LoggedInUser } from '../../common/types';
import { isSquareList } from '../../client/utils/list';

const PAGE_SIZE = 25;

/**
 * The function loads the list's first 25 cards, and populates rest of the
 * list's unloaded content with null.
 * This is done beforehand to ensure working navigation with even large lists in
 * a simple manner.
 */
async function apiListToEpisodesList(
  apiList: List,
  authFetch: AuthFetch,
  itemId: string,
  seasonIndex: number,
  listIndex: number,
  applicationResolution: ApplicationResolution,
  isAuthenticated: boolean,
  geoLocation: GeoLocation
): Promise<EpisodesList> {
  const { data: cards = [], meta } = await getList({
    authFetch,
    url: apiList.source.uri,
    paginationData: {
      limit: PAGE_SIZE,
      offset: 0,
    },
    isAuthenticated,
  });

  const listStyle = apiList.style;

  const episodes: Episode[] = Array.from(
    { length: meta?.count ?? 0 },
    (_, episodeIndex) =>
      cardToEpisode(
        cards[episodeIndex],
        itemId,
        seasonIndex,
        listIndex,
        listStyle,
        episodeIndex,
        applicationResolution,
        geoLocation
      )
  );

  return {
    uri: apiList.source.uri,
    episodes,
    listStyle,
  };
}

async function tabToSeason(
  tab: Tab,
  authFetch: AuthFetch,
  itemId: string,
  seasonIndex: number,
  applicationResolution: ApplicationResolution,
  isAuthenticated: boolean,
  geoLocation: GeoLocation
): Promise<EpisodesSeason> {
  return {
    title: tab.title ?? '',
    lists: await Promise.all(
      (tab.content ?? [])
        .filter((c): c is List => isList(c))
        .map((list, listIndex) =>
          apiListToEpisodesList(
            list,
            authFetch,
            itemId,
            seasonIndex,
            listIndex,
            applicationResolution,
            isAuthenticated,
            geoLocation
          )
        )
    ),
    analytics: tab.analytics,
  };
}

export const initializeEpisodesViewThunk = createAsyncThunk(
  'episodes/loadView',
  async (
    args: {
      itemView: View;
      itemId: string;
      navigate: NavigateFunctionWithState;
      user: LoggedInUser | null;
    },
    {
      extra: {
        authFetch,
        appFeatures: { applicationResolution },
      },
      getState,
    }
  ) => {
    const { itemView, itemId } = args;

    const { episodesViews, currentAddress } = getState();

    if (episodesViews[itemId] !== undefined) {
      return episodesViews[itemId];
    }

    const episodesUri = itemView?.header?.controls?.find(c =>
      c.destination?.uri.includes('episodes')
    )?.destination?.uri;

    if (!episodesUri) {
      throw new NotFoundError(
        'No episodes uri in item view, while requesting episodes view'
      );
    }
    const geoLocation = currentAddress.geoLocation;
    const isAuthenticated = Boolean(args.user);

    const episodesView = await getEpisodes(
      authFetch,
      episodesUri,
      isAuthenticated
    );
    if (!episodesView.data?.tabs) return;

    const seasons = await Promise.all(
      episodesView.data.tabs
        .filter(t => t.content !== undefined && t.content.some(isList))
        .map((tab, tabIndex) =>
          tabToSeason(
            tab,
            authFetch,
            itemId,
            tabIndex,
            applicationResolution,
            isAuthenticated,
            geoLocation
          )
        )
    );

    return {
      analytics: episodesView.data.analytics,
      title: episodesView.data.title ?? '',
      itemId,
      seasons,
    };
  }
);

export const loadEpisodesViewThunk =
  (
    itemView: View,
    itemId: string,
    navigate: NavigateFunctionWithState,
    user: LoggedInUser | null
  ) =>
  async (dispatch: Dispatch) => {
    return dispatch(
      initializeEpisodesViewThunk({ itemView, itemId, navigate, user })
    )
      .unwrap()
      .catch();
  };

/**
 * Function to transform a card from API-response to an episode-object to be consumed by the episodes-view component.
 * If {@link card} is undefined, the data-property of the return-value will be set null.
 * Instead of returning undefined in case of a missing card, returning "empty" (data: null) episode is done in order to
 * - signal an unloaded card for lazy loading
 * - ensure functioning navigation even when the episode is not loaded
 */
function cardToEpisode(
  card: Card | undefined,
  viewId: string,
  seasonIndex: number,
  listIndex: number,
  listStyle: ListStyle | undefined,
  cardIndex: number,
  applicationResolution: ApplicationResolution,
  geoLocation: GeoLocation
): Episode {
  if (!card) {
    return {
      viewId,
      seasonIndex,
      listIndex,
      listStyle,
      episodeIndex: cardIndex,
      data: null, // = "unloaded card" for lazy loading and navigation
    };
  }

  const cardRegion = getRegion(card.labels || []);
  const isGeoBlocked = getIsGeoBlocked(geoLocation, cardRegion);

  const isBroadcasting = isCurrentBroadcastCard(card);

  const cardDestinationPointer = card.controls?.find(isNavigator)?.destination;
  const cardItemId =
    cardDestinationPointer && idFromPointer(cardDestinationPointer);
  const isSquareImage = listStyle?.image === '1:1';
  const imageVar = isSquareImage
    ? imageVariations.MEDIUM_1_1
    : imageVariations.MEDIUM_16_9;
  return {
    viewId,
    seasonIndex,
    listIndex,
    listStyle,
    episodeIndex: cardIndex,
    data: {
      title: card.title,
      image: card.image,
      pointer: card.pointer,
      description: card.description,
      labels: card.labels,
      infoUrl: cardItemId ? routePathsById.info(cardItemId) : undefined,
      isBroadcasting,
      isGeoBlocked,
      episodeId: cardItemId,
      imageUrl: getUrlAndCache({
        image: card.image,
        applicationResolution,
        imageVariation: imageVar,
      }),
      progress: isBroadcasting
        ? getLiveCardUiProgressBar(card, isSquareList(listStyle))
        : getContinueCardUiProgressBarFromLabels(card, true),
    },
  };
}

const listsLoading: Record<string, Set<number>> = {};

function addLoadingList(listStoreId: string, episodeOffset: number) {
  if (!listsLoading[listStoreId]) {
    listsLoading[listStoreId] = new Set<number>();
  }

  listsLoading[listStoreId]?.add(episodeOffset);
}
function removeLoadingList(listStoreId: string, episodeOffset: number) {
  listsLoading[listStoreId]?.delete(episodeOffset);
}

export const fetchEpisodePageThunk = createAsyncThunk(
  'episodes/load-episode-page',
  async (
    args: { payload: Omit<Episode, 'data'>; user: LoggedInUser | null },
    {
      getState,
      extra: {
        authFetch,
        appFeatures: { applicationResolution },
      },
    }
  ) => {
    const { viewId, seasonIndex, listIndex, episodeIndex } = args.payload;
    const { currentAddress } = getState();
    const geoLocation = currentAddress.geoLocation;
    const list = getListSelector(seasonIndex, listIndex, viewId)(getState());
    const listUri = list?.uri;
    const listStyle = list?.listStyle;
    if (!listUri || !list) return;

    const listLoadingId = `${seasonIndex}-${listIndex}`;
    const offset = Math.floor(episodeIndex / PAGE_SIZE) * PAGE_SIZE;

    try {
      const listResponsePromise = getList({
        authFetch,
        url: listUri,
        paginationData: {
          limit: PAGE_SIZE,
          offset,
        },
        isAuthenticated: Boolean(args.user),
      });

      addLoadingList(listLoadingId, offset);

      const episodes = (await listResponsePromise).data?.map(
        (card, cardIndex) =>
          cardToEpisode(
            card,
            viewId,
            seasonIndex,
            listIndex,
            listStyle,
            cardIndex + offset,
            applicationResolution,
            geoLocation
          )
      );

      removeLoadingList(listLoadingId, episodeIndex);
      return episodes;
    } catch (e) {
      removeLoadingList(listLoadingId, episodeIndex);
      throw e;
    }
  },
  {
    condition(args, { getState }) {
      const { seasonIndex, listIndex, viewId, episodeIndex } = args.payload;
      const listStoreId = `${seasonIndex}-${listIndex}`;
      const listUri = getListSelector(
        seasonIndex,
        listIndex,
        viewId
      )(getState());

      const isLoading = Array.from(
        listsLoading[listStoreId]?.values() ?? []
      ).some(
        offset => offset <= episodeIndex && episodeIndex < offset + PAGE_SIZE
      );

      return !(isLoading || !listUri);
    },
  }
);

function getEpisodesToLoad(
  focusedEpisode: Omit<Episode, 'data'>,
  viewEpisodes: Episode[]
) {
  const focusedEpisodeIndex = viewEpisodes.findIndex(
    e =>
      e.seasonIndex === focusedEpisode.seasonIndex &&
      e.listIndex === focusedEpisode.listIndex &&
      e.episodeIndex === focusedEpisode.episodeIndex
  );

  if (focusedEpisodeIndex < 0) {
    return [];
  }

  const bufferSize = 5;
  const bufferStart = Math.max(0, focusedEpisodeIndex - bufferSize);
  const bufferEnd = focusedEpisodeIndex + bufferSize;

  return viewEpisodes
    .slice(bufferStart, bufferEnd)
    .filter(e => e.data === null);
}

export const fetchMissingEpisodesThunk = createAsyncThunk(
  'episodes/load-missing-episodes',
  async (
    args: { payload: Omit<Episode, 'data'>; user: LoggedInUser | null },
    { getState, dispatch }
  ) => {
    const allEpisodes = getEpisodesSelector(args.payload.viewId)(getState());

    const pagePromises = getEpisodesToLoad(args.payload, allEpisodes).map(
      episode =>
        dispatch(fetchEpisodePageThunk({ payload: episode, user: args.user }))
    );

    await Promise.all(pagePromises);
  },
  {
    condition({ payload }, { getState }) {
      const allEpisodes = getEpisodesSelector(payload.viewId)(getState());

      return Boolean(getEpisodesToLoad(payload, allEpisodes).length);
    },
  }
);
